import {
  getState,
  updateForm,
  onlyIfAutoForm,
  formSelector,
  useSelector,
} from '../../../../store.js'
import {isPresent} from '../../../../common/utils.js'
import {NEW_ID} from '../../../../common/constants/SettingsPanels.js'
import {WALMART} from '../../../../common/constants/CartVendorOptions.js'
import {cartSelector} from '../../../../data/carts.js'
import TextInput from '../../Common/TextInput.js'
import Checkbox from '../../Common/Checkbox.js'
import Link from '../../../../common/components/Link/Link.js'
import FormList from '../../Common/FormList.js'
import FormListItem from '../../Common/FormListItem.js'
import VendorForm from './VendorForm.js'
import NameInput from '../NameInput.js'
import PackingListSelect from '../PackingListSelect.js'
import AuthorizeHelper from '../AuthorizeHelper.js'
import LockWriteBack from '../LockWriteBack.js'
import {
  cartFormErrorsSelector,
  baseNewCartParamsSelector,
  baseEditCartParamsSelector,
  baseCartErrorsSelector,
  baseSaveParamsSelector,
} from '../salesChannelsSelectors.js'
import RevisionConfig from '../RevisionConfig.js'
import SyncConfig from '../SyncConfig.js'
import {canUseWalmartOAuthSelector} from '../../../../data/company.js'
import ReauthorizeButton from '../ReauthorizeButton.js'
import {redirect} from '../../../../common/location.js'
import verde from '../../../../common/verde.js'

export function setUpCartForm({cartID, formName}) {
  return {
    formName,
    initialForm: {
      ...initialFormSelector(getState(), {cartID}),
      errorsSelector,
      saveParamsSelector,
      serverParamErrorsSelector,
      authorize,
      revoke,
    },
  }
}

export function getAuthMethod(cart) {
  const {consumer_id, client_id, partner_id} = cart?.vendor_config || {}

  return partner_id
    ? 'o_auth'
    : client_id
      ? 'client_id'
      : consumer_id
        ? 'consumer_id'
        : 'o_auth'
}

// can have v2 only (none)
// can have v3 only (new/old_v3)
// can upgrade to v3 from v2 (old_v2)
export function canUpgradeAuthMethodSelector(state, {formName}) {
  const canUseWalmartOAuth = canUseWalmartOAuthSelector(state)
  const form = formSelector(state, {formName})
  const isNew = form.id === NEW_ID

  return (
    !isNew &&
    (canUseWalmartOAuth
      ? form.authMethod !== 'o_auth'
      : form.authMethod === 'consumer_id')
  )
}

export function errorsSelector(state, {formName}) {
  const form = formSelector(state, {formName})
  const errors = baseCartErrorsSelector(state, {formName})

  if (!form) {
    return errors
  }

  if (form.authMethod === 'consumer_id' && !isPresent(form.consumerID)) {
    errors.consumerID = 'Consumer ID is required'
    errors.preventSave = true
  }

  if (form.authMethod === 'consumer_id' && !isPresent(form.privateKey)) {
    errors.privateKey = 'Private Key is required'
    errors.preventSave = true
  }

  if (form.authMethod === 'client_id' && !isPresent(form.clientID)) {
    errors.clientID = 'Client ID is required'
    errors.preventSave = true
  }

  if (form.authMethod === 'client_id' && !isPresent(form.clientSecret)) {
    errors.clientSecret = 'Client Secret is required'
    errors.preventSave = true
  }

  if (
    form.authMethod !== 'o_auth' &&
    form.id === NEW_ID &&
    !form.hasAcknowledged
  ) {
    errors.hasAcknowledged = 'Must acknowledge'
    errors.preventSave = true
  }

  return errors
}

export function initialFormSelector(state, {cartID}) {
  const canUseWalmartOAuth = canUseWalmartOAuthSelector(state)

  if (cartID === NEW_ID) {
    return {
      ...baseNewCartParamsSelector(state, {vendor: WALMART}),
      authMethod: canUseWalmartOAuth ? 'o_auth' : 'client_id',
      clientID: '',
      clientSecret: '',
      consumerID: '',
      privateKey: '',
      partner_id: '',
      hasAcknowledged: false,
    }
  }

  const cart = cartSelector(getState(), {cartID})

  return {
    ...baseEditCartParamsSelector(getState(), {cartID}),
    authMethod: getAuthMethod(cart),
    clientID: cart.vendor_config.client_id || '',
    clientSecret: cart.vendor_config.client_secret || '',
    consumerID: cart.vendor_config.consumer_id || '',
    privateKey: cart.vendor_config.private_key || '',
    partner_id: cart.vendor_config.partner_id || '',
  }
}

export function serverParamErrorsSelector(state, {formName}) {
  const {serverError, authMethod} = formSelector(state, {formName})
  const errors = {}

  if (authMethod === 'consumer_id' && serverError.param === 'consumer_id') {
    errors.consumerID = serverError.error_message
  } else if (
    authMethod === 'consumer_id' &&
    serverError.param === 'private_key'
  ) {
    errors.privateKey = serverError.error_message
  } else if (authMethod === 'client_id' && serverError.param === 'client_id') {
    errors.clientID = serverError.error_message
  } else if (
    authMethod === 'client_id' &&
    serverError.param === 'client_secret'
  ) {
    errors.clientSecret = serverError.error_message
  }

  return errors
}

export function saveParamsSelector(state, {formName}) {
  const form = formSelector(state, {formName})

  return {
    ...baseSaveParamsSelector(state, {formName}),
    client_id: form.clientID,
    client_secret: form.clientSecret,
    consumer_id: form.consumerID,
    private_key: form.privateKey,
  }
}

export function authorize(formName) {
  const form = formSelector(getState(), {formName})

  const params =
    form.id === NEW_ID
      ? {}
      : {
          cart_id: form.id,
        }

  return redirect('', ['verde', 'walmart', 'start'], params)
}

export async function revoke(formName) {
  const form = formSelector(getState(), {formName})

  if (form.id === NEW_ID) {
    return
  }

  const {json} = await verde.post(`/walmart/revoke/${form.id}`)

  return json
}

function Walmart({form, formName}) {
  const isNew = form.id === NEW_ID
  const errors = useSelector((state) =>
    cartFormErrorsSelector(state, {formName}),
  )
  const canUpgradeAuthMethod = useSelector((state) =>
    canUpgradeAuthMethodSelector(state, {formName}),
  )
  const canUseWalmartOAuth = useSelector(canUseWalmartOAuthSelector)
  const cart = useSelector((state) => cartSelector(state, {cartID: form.id}))

  return (
    <VendorForm>
      <div className="medium-6 columns">
        <FormList>
          {!isNew && (
            <>
              <LockWriteBack formName={formName} />
              <NameInput formName={formName} />
              {canUseWalmartOAuth && (
                <ReauthorizeButton
                  formName={formName}
                  hasRevoke={
                    cart.vendor_config.refresh_token !== 'revoked' &&
                    form.authMethod === 'o_auth'
                  }
                />
              )}
            </>
          )}
          {!canUseWalmartOAuth && canUpgradeAuthMethod && (
            <FormListItem>
              <Checkbox
                id="show_new_auth_creds"
                label="Use v3 authentication"
                checked={form.authMethod === 'client_id'}
                onChange={(checked) =>
                  updateForm(formName, {
                    authMethod: checked ? 'client_id' : 'consumer_id',
                  })
                }
              />
            </FormListItem>
          )}
          {canUseWalmartOAuth ? null : form.authMethod === 'client_id' ? (
            <>
              <FormListItem className="divider--top">
                <TextInput
                  name="client_id"
                  label="Client ID"
                  required
                  placeholder="00112233-4455-6677-8899-aabbccddeeff"
                  value={form.clientID || ''}
                  errorMessage={form.hasChanged_clientID && errors.clientID}
                  onChange={(event) =>
                    updateForm(formName, {
                      clientID: event.target.value,
                      hasChanged_clientID: true,
                    })
                  }
                />
              </FormListItem>
              <FormListItem>
                <TextInput
                  type="password"
                  name="client_secret"
                  label="Client Secret"
                  required
                  value={form.clientSecret || ''}
                  errorMessage={
                    form.hasChanged_clientSecret && errors.clientSecret
                  }
                  onChange={(event) =>
                    updateForm(formName, {
                      clientSecret: event.target.value,
                      hasChanged_clientSecret: true,
                    })
                  }
                />
              </FormListItem>
            </>
          ) : form.authMethod === 'consumer_id' ? (
            <>
              <FormListItem className="divider--top">
                <TextInput
                  name="consumer_id"
                  label="Consumer ID"
                  required
                  placeholder="00112233-4455-6677-8899-aabbccddeeff"
                  value={form.consumerID || ''}
                  errorMessage={form.hasChanged_consumerID && errors.consumerID}
                  onChange={(event) =>
                    updateForm(formName, {
                      consumerID: event.target.value,
                      hasChanged_consumerID: true,
                    })
                  }
                />
              </FormListItem>
              <FormListItem>
                <TextInput
                  type="password"
                  name="private_key"
                  label="Private Key"
                  required
                  value={form.privateKey || ''}
                  errorMessage={form.hasChanged_privateKey && errors.privateKey}
                  onChange={(event) =>
                    updateForm(formName, {
                      privateKey: event.target.value,
                      hasChanged_privateKey: true,
                    })
                  }
                />
              </FormListItem>
            </>
          ) : null}
          {!canUseWalmartOAuth && isNew && (
            <FormListItem>
              <Checkbox
                id="acknowledge"
                label="I’m approved to sell on Walmart"
                checked={form.hasAcknowledged || false}
                onChange={(checked) =>
                  updateForm(formName, {
                    hasAcknowledged: checked,
                  })
                }
              />
            </FormListItem>
          )}
          {isNew && (
            <FormListItem className="divider--top">
              <p className="fs-00 margin-bottom-10">
                {'Not yet approved? '}
                <Link
                  href="https://marketplace.walmart.com/apply/?id=00161000010cMpN"
                  newWindow
                >
                  <strong>Go apply →</strong>
                </Link>
              </p>
            </FormListItem>
          )}
          {!isNew && (
            <>
              <PackingListSelect className="divider--top" formName={formName} />
              <RevisionConfig className="divider--top" formName={formName} />
              <SyncConfig className="divider--top" formName={formName} />
            </>
          )}
        </FormList>
      </div>
      <div className="medium-5 columns">
        <AuthorizeHelper
          vendorName="Walmart"
          href="https://support.ordoro.com/walmart-ordoro-setup/"
        />
      </div>
    </VendorForm>
  )
}

export default onlyIfAutoForm(Walmart, setUpCartForm)
